/* eslint-disable react/prop-types */
/* eslint-disable no-unused-vars */
import React, { useState, useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Grid from '@material-ui/core/Grid'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import TablePagination from '@material-ui/core/TablePagination'
import Paper from '@material-ui/core/Paper'
import Tabs from '@material-ui/core/Tabs'
import Tab from '@material-ui/core/Tab'
import MenuItem from '@material-ui/core/MenuItem'
import Select from '@material-ui/core/Select'
import FormControl from '@material-ui/core/FormControl'
import InputLabel from '@material-ui/core/InputLabel'
import Chip from '@material-ui/core/Chip'
import { makeStyles } from '@material-ui/core/styles'

import FlashContext from '../../components/contexts/FlashContext'
import Loading from '../../components/loading/Loading'
import api from '../../service/axios/api'
import { formatCurrency } from '../../service/utils'

// ─────────────────────────────────────────────────────────────
// Estilos
// ─────────────────────────────────────────────────────────────
const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  filtersRow: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
  },
  formControl: {
    minWidth: 140,
  },
  tabs: {
    marginBottom: theme.spacing(2),
    borderBottom: '1px solid #e0e0e0',
  },
  tabFixed: {
    '&.Mui-selected': {
      color: '#d32f2f',
      fontWeight: 700,
    },
  },
  tabVariable: {
    '&.Mui-selected': {
      color: '#1976d2',
      fontWeight: 700,
    },
  },
  tableHeader: {
    backgroundColor: '#f5f5f5',
    '& th': {
      fontWeight: 700,
      fontSize: '0.85rem',
      color: '#555',
    },
  },
  totalRow: {
    backgroundColor: '#fafafa',
    '& td': {
      fontWeight: 700,
    },
  },
  chipFixed: {
    backgroundColor: '#fdecea',
    color: '#d32f2f',
    fontWeight: 600,
  },
  chipVariable: {
    backgroundColor: '#e3f2fd',
    color: '#1976d2',
    fontWeight: 600,
  },
  summaryCard: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  summaryItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
  summaryLabel: {
    fontSize: '0.75rem',
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  summaryValue: {
    fontSize: '1.1rem',
    fontWeight: 700,
  },
  fixedColor: { color: '#d32f2f' },
  variableColor: { color: '#1976d2' },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(6),
    color: '#aaa',
  },
}))

// ─────────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────────
export default function FixedVariableExpenses() {
  const classes = useStyles()
  const { notifyError } = useContext(FlashContext)

  const today = new Date()
  const currentYear = today.getFullYear()

  const monthOptions = [
    { value: 'all', label: 'Todos' },
    { value: '1',   label: 'Janeiro' },
    { value: '2',   label: 'Fevereiro' },
    { value: '3',   label: 'Março' },
    { value: '4',   label: 'Abril' },
    { value: '5',   label: 'Maio' },
    { value: '6',   label: 'Junho' },
    { value: '7',   label: 'Julho' },
    { value: '8',   label: 'Agosto' },
    { value: '9',   label: 'Setembro' },
    { value: '10',  label: 'Outubro' },
    { value: '11',  label: 'Novembro' },
    { value: '12',  label: 'Dezembro' },
  ]

  const yearOptions = Array.from({ length: 5 }, (_, i) => ({
    value: (currentYear - i).toString(),
    label: (currentYear - i).toString(),
  }))

  // ── Estado ──
  const [activeTab, setActiveTab]   = useState(0) // 0 = Fixa, 1 = Variável
  const [data, setData]             = useState([])
  const [loading, setLoading]       = useState(false)
  const [page, setPage]             = useState(0)
  const [total, setTotal]           = useState(0)
  const [itens, setItens]           = useState(10)
  const [month, setMonth]           = useState('all')
  const [year, setYear]             = useState(currentYear.toString())
  const [totalAmount, setTotalAmount] = useState(0)

  const currentType = activeTab === 0 ? 'Fixa' : 'Variável'

  // ── Busca ──
  const searchData = async () => {
    setLoading(true)
    try {
      const response = await api.fixedVariableExpenses.getAll({
        type: currentType,
        page: page + 1,
        limit: itens,
        month,
        year,
      })

      setData(response.data.expenses)
      setTotal(response.data.total_items)

      // Soma total da página atual (ou receba do backend se implementar)
      const sum = response.data.expenses.reduce(
        (acc, item) => acc + parseFloat(item.amount || 0), 0
      )
      setTotalAmount(sum)
    } catch (error) {
      notifyError(error.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    searchData()
  }, [page, itens, activeTab, month, year])

  // ── Handlers ──
  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue)
    setPage(0)
  }

  const handleChangePage = (_, newPage) => setPage(newPage)

  const handleChangeRowsPerPage = (e) => {
    setItens(parseInt(e.target.value, 10))
    setPage(0)
  }

  // ── Formatação de data ──
  const formatDate = (date) => {
    if (!date) return '-'
    return new Date(date).toLocaleDateString('pt-BR')
  }

  return (
    <>
      <Loading open={loading} msg="Carregando despesas..." />

      <Grid container className={classes.root}>

        {/* Título */}
        <Grid item xs={12} className={classes.header}>
          <h3 style={{ margin: 0 }}>Despesas Fixas e Variáveis</h3>
        </Grid>

        {/* Tabs Fixa / Variável */}
        <Grid item xs={12}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            className={classes.tabs}
            TabIndicatorProps={{
              style: { backgroundColor: activeTab === 0 ? '#d32f2f' : '#1976d2' },
            }}
          >
            <Tab label="Despesas Fixas"    className={classes.tabFixed} />
            <Tab label="Despesas Variáveis" className={classes.tabVariable} />
          </Tabs>
        </Grid>

        {/* Filtros */}
        <Grid item xs={12} className={classes.filtersRow}>
          <FormControl variant="outlined" size="small" className={classes.formControl}>
            <InputLabel>Mês</InputLabel>
            <Select
              value={month}
              onChange={(e) => { setMonth(e.target.value); setPage(0) }}
              label="Mês"
            >
              {monthOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl variant="outlined" size="small" className={classes.formControl}>
            <InputLabel>Ano</InputLabel>
            <Select
              value={year}
              onChange={(e) => { setYear(e.target.value); setPage(0) }}
              label="Ano"
            >
              {yearOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Totalizador */}
        <Grid item xs={12} className={classes.summaryCard}>
          <div className={classes.summaryItem}>
            <span className={classes.summaryLabel}>Total de registros</span>
            <span className={classes.summaryValue}>{total}</span>
          </div>
          <div className={classes.summaryItem}>
            <span className={classes.summaryLabel}>Total do período</span>
            <span
              className={`${classes.summaryValue} ${
                activeTab === 0 ? classes.fixedColor : classes.variableColor
              }`}
            >
              {formatCurrency(totalAmount)}
            </span>
          </div>
        </Grid>

        {/* Tabela */}
        <Grid item xs={12}>
          <TableContainer component={Paper} elevation={1}>
            <Table size="small">
              <TableHead className={classes.tableHeader}>
                <TableRow>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Observação</TableCell>
                  <TableCell>Centro de Custo</TableCell>
                  <TableCell>Vencimento</TableCell>
                  <TableCell align="right">Valor</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {data.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className={classes.emptyState}>
                      Nenhuma despesa encontrada para o período selecionado.
                    </TableCell>
                  </TableRow>
                ) : (
                  data.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        <Chip
                          label={row.type}
                          size="small"
                          className={
                            row.type === 'Fixa'
                              ? classes.chipFixed
                              : classes.chipVariable
                          }
                        />
                      </TableCell>
                      <TableCell>{row.observation || '-'}</TableCell>
                      <TableCell>{row.CostCenter?.name || '-'}</TableCell>
                      <TableCell>{formatDate(row.due_date)}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(parseFloat(row.amount || 0))}
                      </TableCell>
                      <TableCell>{row.status || '-'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={total}
              rowsPerPage={itens}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Linhas por página"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}–${to} de ${count}`
              }
            />
          </TableContainer>
        </Grid>

      </Grid>
    </>
  )
}
